// LlmCostDashboard.jsx
//
// The two-chart cost panel that hangs off the LLM cost-gate page.
//
//   TOP chart  — historical $ + tokens with three series:
//                  - internal $ (our aiCalls per-call telemetry)
//                  - provider truth $ (Anthropic + OpenAI billing API)
//                  - tokens (in + out summed, secondary axis)
//                Right-axis = tokens; left-axis = USD.
//
//   BOTTOM chart — projection. Same X scale extended into the future
//                  with vertical markers at day/week/month/year. The
//                  projection line is the trailing-window rate
//                  extrapolated forward. A second "actual landings"
//                  line walks the past projections vs what landed.
//
//   Variance table — below the projection chart. Lists each past
//                    snapshot's predicted $/day against the actual
//                    $ that landed in the following 24h window with
//                    a per-row correction $/% delta.
//
// Polls every 30s. Window selector at the top: 24h / 7d / 30d / 1y.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine,
} from 'recharts';
import { get } from '../../api';

const WINDOWS = [
  { key: '24h', label: '24 hours' },
  { key: '7d',  label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '1y',  label: '1 year' },
];

const POLL_MS = 30_000;

function fmtUsd(v) {
  const n = Number(v) || 0;
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1)    return `$${n.toFixed(3)}`;
  if (n < 100)  return `$${n.toFixed(2)}`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtTok(v) {
  const n = Number(v) || 0;
  if (n < 1000) return String(Math.round(n));
  if (n < 1000000) return `${(n / 1000).toFixed(1)}K`;
  return `${(n / 1000000).toFixed(2)}M`;
}
function fmtPct(v) {
  const n = Number(v) || 0;
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

// Bucket provider rows into the same chart shape as history. We
// match on the closest bucket label so the overlay aligns cleanly.
function overlayProviderTruth(history, providerRows) {
  if (!Array.isArray(history) || !history.length) return history;
  // Build a quick lookup of bucket-label → row from history.
  const out = history.map((h) => ({ ...h, providerUsd: 0 }));
  const labelMs = (lab) => {
    // Re-parse bucket label back to a date for proximity comparison.
    // Hour: '2026-05-13 10:00'; day: '2026-05-13'; week: '2026-W19'.
    if (/^\d{4}-\d{2}-\d{2} \d{2}:00$/.test(lab)) return new Date(lab.replace(' ', 'T') + 'Z').getTime();
    if (/^\d{4}-\d{2}-\d{2}$/.test(lab))         return new Date(lab + 'T00:00:00Z').getTime();
    return NaN;
  };
  for (const r of providerRows || []) {
    const ts = new Date(r.bucketStart).getTime();
    if (isNaN(ts)) continue;
    // Find nearest history bucket.
    let bestIdx = -1, bestDelta = Infinity;
    for (let i = 0; i < out.length; i++) {
      const labMs = labelMs(out[i].bucket);
      if (isNaN(labMs)) continue;
      const d = Math.abs(labMs - ts);
      if (d < bestDelta) { bestDelta = d; bestIdx = i; }
    }
    if (bestIdx >= 0) out[bestIdx].providerUsd = (out[bestIdx].providerUsd || 0) + (Number(r.usd) || 0);
  }
  return out;
}

// Future buckets for the projection chart. Given a window like
// '7d', we extrapolate the same bucket cadence forward by the same
// number of slots so the chart shows past + future symmetrically.
function buildProjectionSeries({ history, projection, window }) {
  const out = (history || []).map((h) => ({
    bucket: h.bucket, usd: h.usd, tokensSum: (h.tokensIn || 0) + (h.tokensOut || 0),
    projectedUsd: null, // null on the past = line stops at "now"
  }));
  if (!projection) return out;
  const futureBuckets = Math.max(6, Math.min(60, (history || []).length || 12));
  // Per-bucket projected $ = horizon $ / horizon buckets, smoothed
  // to the current bucket cadence. We use the day horizon for the
  // 24h / 7d windows, month for 30d, year for 1y.
  let perBucket = 0;
  if (window === '24h' && projection.day)   perBucket = projection.day.usd   / 24;
  if (window === '7d'  && projection.day)   perBucket = projection.day.usd   / 24;
  if (window === '30d' && projection.month) perBucket = projection.month.usd / 30;
  if (window === '1y'  && projection.year)  perBucket = projection.year.usd  / 52;

  for (let i = 0; i < futureBuckets; i++) {
    out.push({ bucket: `+${i + 1}`, usd: null, tokensSum: null, projectedUsd: perBucket });
  }
  return out;
}

function Tile({ title, value, sub, tone = 'neutral' }) {
  const bg = tone === 'good' ? '#e7f5ec' : tone === 'warn' ? '#fff4cc' : tone === 'bad' ? '#fdecea' : '#fafbfc';
  const fg = tone === 'good' ? '#1e6b3e' : tone === 'warn' ? '#856404' : tone === 'bad' ? '#a71d2a' : '#15172b';
  return (
    <div style={{ flex: 1, padding: 10, background: bg, color: fg, borderRadius: 6, minWidth: 130 }}>
      <div style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{value}</div>
      {sub != null && <div style={{ fontSize: 11, opacity: 0.7 }}>{sub}</div>}
    </div>
  );
}

export default function LlmCostDashboard({ companyFilter = null, markup = 1 } = {}) {
  const [window, setWindow] = useState('7d');
  const [series,   setSeries]   = useState(null);
  const [provider, setProvider] = useState(null);
  const [proj,     setProj]     = useState(null);
  const [err,      setErr]      = useState(null);

  const isSuper = useSelector((s) => !!(s.user && s.user.userInfo && s.user.userInfo.superAdmin));

  // Super-admin company filter is forwarded into every cost endpoint
  // as ?company=<id>. The backend tenant-scopes the data accordingly
  // (still raw figures, since super-admin sees true cost; the FE
  // applies the markup for display).
  const companyParam = companyFilter ? `&company=${encodeURIComponent(companyFilter)}` : '';

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      // Provider-truth is org-level (the provider doesn't know our
      // tenancy) → super-admin only. Company admins skip the fetch
      // entirely so it doesn't 403 in the console.
      const calls = [
        get(`/api/admin/llm-budget/series?window=${window}${companyParam}`),
        get(`/api/admin/llm-budget/projection${companyParam ? '?company=' + encodeURIComponent(companyFilter) : ''}`),
      ];
      if (isSuper && !companyFilter) {
        // Provider truth is only meaningful org-wide; hide when filtered.
        calls.push(get(`/api/admin/llm-budget/provider-truth?window=${window}`));
      }
      const results = await Promise.all(calls);
      setSeries(results[0] && results[0].data);
      setProj  (results[1] && results[1].data);
      setProvider(isSuper && !companyFilter && results[2] ? results[2].data : null);
    } catch (e) {
      setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Failed to load');
    }
  }, [window, isSuper, companyParam, companyFilter]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { const t = setInterval(refresh, POLL_MS); return () => clearInterval(t); }, [refresh]);

  const topChartData = useMemo(() => {
    if (!series || !series.history) return [];
    const merged = series.history.map((h) => ({
      bucket: h.bucket,
      usd: Number(h.usd) || 0,
      tokensSum: (Number(h.tokensIn) || 0) + (Number(h.tokensOut) || 0),
    }));
    return overlayProviderTruth(merged, provider && provider.rows);
  }, [series, provider]);

  const projChartData = useMemo(() => {
    if (!series || !series.history) return [];
    return buildProjectionSeries({
      history:    series.history,
      projection: proj && proj.latest && proj.latest.projection,
      window,
    });
  }, [series, proj, window]);

  // Live tile rollups from the latest snapshot.
  const latest = proj && proj.latest;
  const live = latest && latest.observed ? latest.observed : { usd: 0, tokensIn: 0, tokensOut: 0, calls: 0 };
  const pday  = latest && latest.projection && latest.projection.day   ? latest.projection.day   : { usd: 0 };
  const pweek = latest && latest.projection && latest.projection.week  ? latest.projection.week  : { usd: 0 };
  const pmth  = latest && latest.projection && latest.projection.month ? latest.projection.month : { usd: 0 };
  const pyr   = latest && latest.projection && latest.projection.year  ? latest.projection.year  : { usd: 0 };

  const variance = (proj && proj.variance) || [];

  return (
    <div className="acc-card" style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <h5 style={{ marginBottom: 0 }}>Live LLM cost — execution + projections</h5>
        <div style={{ display: 'flex', gap: 4 }}>
          {WINDOWS.map((w) => (
            <button
              key={w.key} type="button"
              onClick={() => setWindow(w.key)}
              className="acc-btn"
              style={{
                fontSize: 12, padding: '4px 10px',
                background: window === w.key ? '#15172b' : '#fff',
                color: window === w.key ? '#fff' : '#15172b',
                borderColor: window === w.key ? '#15172b' : '#d1d5db',
              }}
            >{w.label}</button>
          ))}
        </div>
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      {/* Markup disclosure for non-super callers. The backend stamps
          markupApplied:true when the platform markup is folded into
          the numbers — we surface it transparently here. */}
      {!isSuper && series && series.markupApplied && (
        <div style={{ fontSize: 11, color: '#666', marginBottom: 10, padding: '6px 10px', background: '#fef3c7', borderRadius: 4, border: '1px solid #fbbf24' }}>
          Cost figures include the platform fee (infrastructure, brand-voice tuning, monitoring, and maintenance). Your underlying provider rate is a portion of what's shown.
        </div>
      )}

      {/* Live tiles. When the super-admin has filtered to one company,
          each tile shows BOTH true cost (raw, big number) and
          with-markup cost (raw × multiplier, sub-line). Otherwise a
          single number — all-companies super-admin sees raw, company-
          admin sees marked-up only (backend already applies it). */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {(() => {
          const showDual = isSuper && companyFilter && markup && markup !== 1;
          const dualSub = (rawUsd, extra) => {
            const marked = (Number(rawUsd) || 0) * markup;
            return showDual
              ? <>with markup ({markup}×): <strong>{fmtUsd(marked)}</strong>{extra ? <> · {extra}</> : null}</>
              : extra;
          };
          return (
            <>
              <Tile title="Last 24h spend"   value={fmtUsd(live.usd)}  sub={dualSub(live.usd, `${fmtTok((live.tokensIn || 0) + (live.tokensOut || 0))} tokens · ${live.calls || 0} calls`)} />
              <Tile title="Projected /day"   value={fmtUsd(pday.usd)}  sub={dualSub(pday.usd,  'extrapolated')} />
              <Tile title="Projected /week"  value={fmtUsd(pweek.usd)} sub={dualSub(pweek.usd, '')} />
              <Tile title="Projected /month" value={fmtUsd(pmth.usd)}  sub={dualSub(pmth.usd,  '')} />
              <Tile title="Projected /year"  value={fmtUsd(pyr.usd)}   sub={dualSub(pyr.usd,   'annualized')} tone="warn" />
            </>
          );
        })()}
      </div>

      {/* TOP chart: historical $ + tokens, with provider-truth overlay */}
      <div style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={topChartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="usd"    tick={{ fontSize: 10 }} tickFormatter={fmtUsd} />
            <YAxis yAxisId="tokens" tick={{ fontSize: 10 }} tickFormatter={fmtTok} orientation="right" />
            <Tooltip formatter={(value, name) => name === 'tokensSum' ? fmtTok(value) : fmtUsd(value)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar  yAxisId="usd"    dataKey="usd"         name="Internal $ (aiCalls)" fill="#0F62FE" />
            {isSuper && (
              <Line yAxisId="usd"    dataKey="providerUsd" name="Provider truth $"     stroke="#F1C21B" strokeWidth={2} dot={false} />
            )}
            <Line yAxisId="tokens" dataKey="tokensSum"   name="Tokens (in+out)"      stroke="#42BE65" strokeWidth={1.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
        {isSuper
          ? <>Internal bars are summed from every `aiCalls` array across {series && series.totals ? series.totals.calls : 0} calls in this window. Provider line is what Anthropic + OpenAI billed (refreshed every 30 min).</>
          : <>Showing usage across your brands only. Refreshes every 30 seconds.</>}
        {isSuper && provider && !provider.configured.anthropic && !provider.configured.openai &&
          <span style={{ marginLeft: 6, color: '#a71d2a' }}>
            Provider keys not set — overlay line will stay flat until you set ANTHROPIC_ADMIN_API_KEY + OPENAI_ADMIN_API_KEY.
          </span>}
      </div>

      {/* BOTTOM chart: projection with day/week/month/year markers */}
      <h6 style={{ marginTop: 18, marginBottom: 6 }}>Projection — current rate → day / week / month / year</h6>
      <div style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={projChartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtUsd} />
            <Tooltip formatter={(value) => fmtUsd(value)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar  dataKey="usd"          name="Actual $ (bucket)"     fill="#0F62FE" />
            <Line dataKey="projectedUsd" name="Projected $ at current rate" stroke="#a71d2a" strokeWidth={2} dot={false} />
            {/* "Now" marker — line between last actual bucket and first projection bucket. */}
            <ReferenceLine x={projChartData.find((d) => d.bucket && String(d.bucket).startsWith('+1'))?.bucket} stroke="#888" strokeDasharray="2 2" label={{ value: 'now', fontSize: 10, fill: '#888', position: 'insideTopLeft' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
        Projection extrapolates the trailing 24h burn rate forward. Past buckets show actual $ landed; right of the dashed "now" marker is the forecast.
      </div>

      {/* Variance: predicted vs actual, with correction */}
      <h6 style={{ marginTop: 18, marginBottom: 6 }}>Variance — what we predicted vs what landed</h6>
      {variance.length === 0 ? (
        <div style={{ fontSize: 12, color: '#888', padding: 10, background: '#fafbfc', borderRadius: 6 }}>
          Not enough snapshots yet. The projection ticker writes every 5 minutes; once the second snapshot is more than 24 hours old this table populates.
        </div>
      ) : (
        <table className="table table-sm" style={{ fontSize: 12 }}>
          <thead><tr>
            <th>Predicted at</th>
            <th style={{ textAlign: 'right' }}>Predicted /day</th>
            <th style={{ textAlign: 'right' }}>Actual /24h</th>
            <th style={{ textAlign: 'right' }}>Delta</th>
            <th style={{ textAlign: 'right' }}>Correction</th>
          </tr></thead>
          <tbody>
            {variance.slice(-20).reverse().map((v, idx) => {
              const tone = v.deltaPct > 20 ? '#a71d2a' : v.deltaPct < -20 ? '#1e6b3e' : '#15172b';
              return (
                <tr key={idx}>
                  <td>{new Date(v.predictedAt).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{fmtUsd(v.predictedDayUsd)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtUsd(v.actualWindowUsd)}</td>
                  <td style={{ textAlign: 'right', color: tone }}>{fmtUsd(v.deltaUsd)}</td>
                  <td style={{ textAlign: 'right', color: tone }}>{fmtPct(v.deltaPct)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Exposed for unit tests on the pure helpers.
LlmCostDashboard._test = { fmtUsd, fmtTok, fmtPct, overlayProviderTruth, buildProjectionSeries };
